import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Save } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RelatorioDiario({ user }) {
  const navigate = useNavigate();
  const [corretor, setCorretor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    data: new Date().toISOString().split('T')[0],
    contatos: 0,
    visitas_1: 0,
    visitas_2: 0,
    captacoes: 0,
    estudou: false,
    usou_c2s: false,
    esteve_na_silver: false,
    maior_dificuldade: ''
  });

  useEffect(() => {
    loadCorretor();
  }, []);

  const loadCorretor = async () => {
    try {
      const response = await axios.get(`${API}/corretores`);
      if (response.data.length > 0) {
        setCorretor(response.data[0]);
      }
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!corretor) {
      toast.error('Perfil de corretor não encontrado');
      return;
    }
    
    setSaving(true);
    
    try {
      await axios.post(`${API}/relatorio-diario`, {
        corretor_id: corretor.id,
        ...formData
      });
      
      toast.success('Relatório salvo com sucesso!');
      
      // Reset form
      setFormData({
        data: new Date().toISOString().split('T')[0],
        contatos: 0,
        visitas_1: 0,
        visitas_2: 0,
        captacoes: 0,
        estudou: false,
        usou_c2s: false,
        esteve_na_silver: false,
        maior_dificuldade: ''
      });
      
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar relatório');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-remax-blue"></div>
      </div>
    );
  }

  if (!corretor) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="text-center">Perfil não encontrado</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-gray-600 mb-4">Você ainda não foi cadastrado no programa.</p>
            <Button onClick={() => navigate('/')} variant="outline">Voltar</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/')}
                data-testid="back-button"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <h1 className="text-xl font-bold" style={{ fontFamily: 'Work Sans', color: '#003DA5' }}>
                  Relatório Diário
                </h1>
                <p className="text-sm text-gray-500">Silver Start - Onboarding</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card>
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Work Sans' }}>Preencha seu relatório de hoje</CardTitle>
            <p className="text-sm text-gray-500 mt-2">
              Este relatório ajuda você e seu mentor a acompanhar seu progresso diário
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Data */}
              <div>
                <Label htmlFor="data">Data</Label>
                <Input
                  id="data"
                  type="date"
                  value={formData.data}
                  onChange={(e) => setFormData({ ...formData, data: e.target.value })}
                  required
                  data-testid="data-input"
                />
              </div>

              {/* Numeros */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contatos">Contatos (ligação/WhatsApp)</Label>
                  <Input
                    id="contatos"
                    type="number"
                    min="0"
                    value={formData.contatos}
                    onChange={(e) => setFormData({ ...formData, contatos: parseInt(e.target.value) || 0 })}
                    data-testid="contatos-input"
                  />
                </div>
                <div>
                  <Label htmlFor="visitas_1">Visitas 1ª vez</Label>
                  <Input
                    id="visitas_1"
                    type="number"
                    min="0"
                    value={formData.visitas_1}
                    onChange={(e) => setFormData({ ...formData, visitas_1: parseInt(e.target.value) || 0 })}
                    data-testid="visitas1-input"
                  />
                </div>
                <div>
                  <Label htmlFor="visitas_2">Visitas 2ª vez</Label>
                  <Input
                    id="visitas_2"
                    type="number"
                    min="0"
                    value={formData.visitas_2}
                    onChange={(e) => setFormData({ ...formData, visitas_2: parseInt(e.target.value) || 0 })}
                    data-testid="visitas2-input"
                  />
                </div>
                <div>
                  <Label htmlFor="captacoes">Captações</Label>
                  <Input
                    id="captacoes"
                    type="number"
                    min="0"
                    value={formData.captacoes}
                    onChange={(e) => setFormData({ ...formData, captacoes: parseInt(e.target.value) || 0 })}
                    data-testid="captacoes-input"
                  />
                </div>
              </div>

              {/* Checkboxes */}
              <div className="space-y-3">
                <label className="flex items-center space-x-2" data-testid="estudou-label">
                  <input
                    type="checkbox"
                    checked={formData.estudou}
                    onChange={(e) => setFormData({ ...formData, estudou: e.target.checked })}
                    className="w-4 h-4"
                    data-testid="estudou-checkbox"
                  />
                  <span>Estudou hoje?</span>
                </label>
                <label className="flex items-center space-x-2" data-testid="c2s-label">
                  <input
                    type="checkbox"
                    checked={formData.usou_c2s}
                    onChange={(e) => setFormData({ ...formData, usou_c2s: e.target.checked })}
                    className="w-4 h-4"
                    data-testid="c2s-checkbox"
                  />
                  <span>Usou o C2S?</span>
                </label>
                <label className="flex items-center space-x-2" data-testid="silver-label">
                  <input
                    type="checkbox"
                    checked={formData.esteve_na_silver}
                    onChange={(e) => setFormData({ ...formData, esteve_na_silver: e.target.checked })}
                    className="w-4 h-4"
                    data-testid="silver-checkbox"
                  />
                  <span>Esteve na Silver hoje?</span>
                </label>
              </div>

              {/* Dificuldade */}
              <div>
                <Label htmlFor="dificuldade">Maior dificuldade do dia</Label>
                <Textarea
                  id="dificuldade"
                  value={formData.maior_dificuldade}
                  onChange={(e) => setFormData({ ...formData, maior_dificuldade: e.target.value })}
                  rows={4}
                  placeholder="Descreva os principais desafios enfrentados hoje..."
                  data-testid="dificuldade-textarea"
                />
              </div>

              {/* Submit */}
              <div className="flex justify-end space-x-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/')}
                  data-testid="cancel-button"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="bg-remax-blue hover:bg-[#002C7A]"
                  disabled={saving}
                  data-testid="save-button"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Salvando...' : 'Salvar Relatório'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="mt-6 bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <p className="text-sm text-blue-900">
              <strong>Dica:</strong> Preencher o relatório diário ajuda a criar consciência sobre suas atividades 
              e permite que seu mentor identifique rapidamente pontos de atenção. 
              Seja honesto e detalhado nas suas respostas!
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
